const Math2D = require("../../core/util/Math2D");

// You should not directly use this file. It's the minimal representation of
// a geometry that GridGeometry turns into a full interface. Use GridGeometry.
// See SquareGridGeometryCore for any special notes about the interfaces for
// these functions.

// Triangular grid sizes are specified in rows and columns. There are
// 2*cols*rows triangular cells, with each row containing 2*cols cells, half in
// each orientation.
// - Cells are identified by [i, j] with 0 <= i < 2*cols and 0 <= j < rows.
// - Vertices are identified by [i, j] with 0 <= i <= cols and 0 <= j <= rows.
// - Edges are identified by [i, j, ydir] with [i, j] a vertex and ydir one of
//   -1, 0, 1. The edge is going right out of the identified vertex and the
//   ydir identifies the y direction the edge is going towards.

// Rough ASCII diagram of cell/vertex coordinates in a 2 by 2 grid.
// 0,0 ----- 1,0 ----- 2,0
//   \       / \       / \
//    \ 0,0 /   \ 2,0 /   \
//     \   / 1,0 \   / 3,0 \
//      \ /       \ /       \
//      0,1 ----- 1,1 ----- 2,1
//      / \       / \       /
//     /   \ 1,1 /   \ 3,1 /
//    / 0,1 \   / 2,1 \   /
//   /       \ /       \ /
// 0,2 ----- 1,2 ----- 2,2

// Constant used in a lot of computations.
const HRT3 = Math.sqrt(3) / 2;

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const TriangleGridGeometryCore = function(cols, rows, options) {
	this.style = "tri";
	this.cols = cols;
	this.rows = rows;
	this.options = options || {};
	this.noLeft = !!this.options.noLeft;
	this.noRight = !!this.options.noRight;
};

// Internal helper. Get range of cell x coordinates in [min, max + 1] form.
const getCellRange = function(core) {
	return [core.noLeft ? 1 : 0, 2 * core.cols - (core.noRight ? 1 : 0)];
};

TriangleGridGeometryCore.prototype.getBaseWidth = function() {
	const range = getCellRange(this);
	return (1 + range[1] - range[0]) / 2;
};

TriangleGridGeometryCore.prototype.getBaseHeight = function() {
	return this.rows * HRT3;
};

// This should return the cells in left-to-right then top-to-bottom order.
TriangleGridGeometryCore.prototype.getAllCells = function() {
	const range = getCellRange(this);
	const result = [];
	for (let j = 0; j < this.rows; j++) {
		for (let i = range[0]; i < range[1]; i++) {
			result.push([i, j]);
		}
	}
	return result;
};

TriangleGridGeometryCore.prototype.convertEdgeZ = function(yDir) {
	return parseInt(yDir);
};

// Order matters: the edges must go clockwise around the cell
TriangleGridGeometryCore.prototype.getCellEdges = function(x, y) {
	// Compute most common x coordinate for base vertex of these edges.
	const mainX = Math.floor(x / 2);
	if ((y % 2 === 0) === (x % 2 === 0)) {
		// Downward-pointing cell.
		return [
			[mainX, y, 0],
			[Math.floor((x + 1) / 2), y + 1, -1],
			[mainX, y, 1],
		];
	} else {
		// Upward-pointing cell
		return [
			[mainX, y + 1, 0],
			[mainX, y + 1, -1],
			[Math.floor((x + 1) / 2), y, 1],
		];
	}
};

TriangleGridGeometryCore.prototype.getEdgeCells = function(x, y, yDir) {
	const yParity = y % 2 ? 1 : 0;
	// Compute most common x coordinate of a cell.
	const mainX = 2 * x + yParity;
	if (yDir === -1) {
		return [
			[mainX - 1, y - 1],
			[mainX, y - 1],
		];
	} else if (yDir === 0) {
		return [
			[mainX, y - 1],
			[mainX, y],
		];
	} else if (yDir === 1) {
		return [
			[mainX - 1, y],
			[mainX, y],
		];
	} else {
		throw new Error("Invalid edge yDir " + yDir);
	}
};

TriangleGridGeometryCore.prototype.getEdgeVertices = function(x, y, yDir) {
	const yParity = y % 2 ? 1 : 0;
	if (yDir === -1) {
		return [
			[x, y],
			[x + yParity, y - 1],
		];
	} else if (yDir === 0) {
		return [
			[x, y],
			[x + 1, y],
		];
	} else if (yDir === 1) {
		return [
			[x, y],
			[x + yParity, y + 1],
		];
	} else {
		throw new Error("Invalid edge yDir " + yDir);
	}
};

// Order matters: the edges must go clockwise around the vertex.
TriangleGridGeometryCore.prototype.getVertexEdges = function(x, y) {
	const yParity = y % 2 ? 1 : 0;
	return [
		[x, y, -1],
		[x, y, 0],
		[x, y, 1],
		[x + yParity - 1, y + 1, -1],
		[x - 1, y, 0],
		[x + yParity - 1, y - 1, 1],
	];
};

TriangleGridGeometryCore.prototype.getCollinearEdge = function(edge, vertex) {
	const [x, y, yDir] = edge;
	const [vx, vy] = vertex;
	const yParity = y % 2 ? 1 : 0;
	const sameBaseVertex = x === vertex[0] && y === vertex[1];
	if (!sameBaseVertex) {
		return [vx, vy, yDir];
	} else if (yDir === 0) {
		// and sameBaseVertex
		return [x - 1, y, 0];
	} else {
		// slanted, sameBaseVertex
		return [x + yParity - 1, y - yDir, yDir];
	}
};

TriangleGridGeometryCore.prototype.getDrawCellRect = function(dim, x, y) {
	const center = this.getDrawCellCenter(dim, x, y);
	return {
		x: center[0] - (HRT3 / 3) * dim.size,
		y: center[1] - (HRT3 / 3) * dim.size,
		w: ((2 * HRT3) / 3) * dim.size,
		h: ((2 * HRT3) / 3) * dim.size,
	};
};

TriangleGridGeometryCore.prototype.getDrawCellCenter = function(dim, x, y) {
	const xNudge = this.noLeft ? -0.5 : 0;
	// Amount past top vertex that center is shifted down, which is based on
	// triangle orientation.
	const yNudge = (HRT3 / 3) * ((x + y) % 2 ? 2 : 1);
	return [
		dim.x + (xNudge + (x + 1) * 0.5) * dim.size,
		dim.y + (yNudge + y * HRT3) * dim.size,
	];
};

TriangleGridGeometryCore.prototype.getDrawVertex = function(dim, x, y) {
	const realX = x + (y % 2 ? 0.5 : 0) + (this.noLeft ? -0.5 : 0);
	return [dim.x + realX * dim.size, dim.y + y * HRT3 * dim.size];
};

TriangleGridGeometryCore.prototype.mouseToGrid = function(dim, mouseX, mouseY) {
	return [
		(mouseX - dim.x) / dim.size + (this.noLeft ? 0.5 : 0),
		(mouseY - dim.y) / (HRT3 * dim.size),
	];
};

TriangleGridGeometryCore.prototype.gridToMouse = function(dim, coordX, coordY) {
	coordX += this.noLeft ? -0.5 : 0;
	return [coordX * dim.size + dim.x, coordY * dim.size * HRT3 + dim.y];
};

TriangleGridGeometryCore.prototype.getMousedCell = function(
	coordX,
	coordY,
	clamp
) {
	const fx = coordX - Math.floor(coordX);
	const fy = coordY - Math.floor(coordY);
	// Flip y axis if needed so all rows can be treated the same.
	const invertedRow = Math.floor(coordY) % 2 !== 0;
	const realFY = invertedRow ? 1 - fy : fy;

	// Preliminary cell coordinates.
	const cell = [Math.floor(2 * coordX), Math.floor(coordY)];
	// Check if we're over slanted cell boundaries and make corrections.
	if (fx < 0.5 && realFY - 2 * fx > 0) {
		cell[0] -= 1;
	} else if (fx >= 0.5 && realFY + 2 * fx < 2) {
		cell[0] -= 1;
	}

	// If clamping, make sure we get a valid x coordinate.
	if (clamp) {
		const range = getCellRange(this);
		cell[0] = Math.max(range[0], Math.min(range[1], cell[0]));
	}

	return cell;
};

TriangleGridGeometryCore.prototype.isDeadCenter = function(
	deadDistance,
	coordX,
	coordY,
	centerX,
	centerY
) {
	const dist = Math2D.pointPointDistance(coordX, coordY, centerX, centerY);
	return dist < deadDistance / 2;
};

TriangleGridGeometryCore.prototype.isDeadVertex = function(
	deadDistance,
	coordX,
	coordY,
	vertexX,
	vertexY
) {
	const dist = Math2D.pointPointDistance(coordX, coordY, vertexX, vertexY);
	return dist < deadDistance / 2;
};

TriangleGridGeometryCore.prototype.isLooseVertexFit = function(
	coordX,
	coordY,
	vertexX,
	vertexY
) {
	const dx = vertexX - coordX;
	const dy = vertexY - coordY;
	const squareDist = dx * dx + 0.75 * dy * dy;
	return squareDist > 0.1;
};

TriangleGridGeometryCore.prototype.isNewEdge = function(
	coordX,
	coordY,
	newEdge,
	lastEdge
) {
	// Later if we want to do some sensitive checking of new edges, we can add this.
	return true;
};

module.exports = TriangleGridGeometryCore;
